import React from 'react';
import { Route } from 'components/dynamic-page/route-matchers/types';
import type { ReadonlyDeep } from 'type-fest';
import type { ReactNode } from 'react';
import type { CountryCode, CountryCodeList } from 'types/countries';
import type { SiteLanguage, SiteNameSpace } from 'constants/locale';
import { ThemeConfig } from 'themes';
import type { Slide } from 'components/autoplay-slides';
import { HtmlTransClassNames, HtmlTransStyleOVerrides, HtmlTransValues } from 'components/html-trans';

export const ZOOCASA_TENANT_NAME = 'zoocasa' as const;
export const EXP_IN_SERVICE_TENANT_NAME = 'expInService' as const;
export const EXP_REALTY_CA_TENANT_NAME = 'exprealtyCA' as const;
export const EXP_REALTY_US_TENANT_NAME = 'exprealtyUS' as const;
export const EXP_COMMERCIAL_TENANT_NAME = 'expCommercial' as const;

export type TenantName = typeof ZOOCASA_TENANT_NAME | typeof EXP_IN_SERVICE_TENANT_NAME | typeof EXP_REALTY_CA_TENANT_NAME | typeof EXP_REALTY_US_TENANT_NAME | typeof EXP_COMMERCIAL_TENANT_NAME;

type FooterColItem = {
    label: string;
    link?: string;
    target?: string;
    isAnchor?: boolean;
  };

  interface FooterImageData {
    src: string;
    alt: string;
  }

export type ExpFooterComponentProps = {
    config: FooterConfig;
  }

export type HeaderConfig = ReadonlyDeep<{
  mainButtonCopy: string,
  authButtonCopy: string,
  hasJoinExpPage: boolean,
  authMenuItems: {
    label: string;
    link?: string;
    id: string;
    signOut?: boolean;
  }[];
  menuItems: {
    label: string;
    link?: string;
    id: string;
    newTab?: boolean;
  }[];
}>;

export type ContentCard = {
    title: string,
    image: string,
    titleColor: string,
    backgroundColor: string,
    content: string,
    link: string,
    desktopTransform?: string,
    tabletTransform?: string,
    translateYDesktop?: string,
    translateYTablet?: string,
    rounded?: boolean,
    altText?: string,
    buttonText?: string,
};

export type TestimonialProps = {
    text: string;
    image: string;
    alt: string;
    name: string;
    company: string;
    key: number;
};

type InternalLinksProps = {
    title: string;
    links: {
        label: string;
        link: string;
    }[]
};

export type HomeConfig = ReadonlyDeep<{
    heroVideo?: string,
    heroImage?: string,
    heroImageAlt?: string,
    joinLink?: string,
    testimonialBlock?: {
        backgroundImage: string,
        heading: string,
        testimonials: TestimonialProps[],
    },
    joinUsSection?: {
        link: string,
        title: string,
        title2: string,
        description: string,
        imageUrl: string,
        buttonText: string,
    },
    contentCards?: {
        sectionTitleParts: string[],
        cards: ContentCard[],
    },
    carouselImages?: string[],
    internalLinks?: InternalLinksProps[],
    autoplaySlides?: {
        title: string;
        description: string;
        buttonText: string;
        buttonLink: string;
        slides: Slide[];
    }
}>;

export type FooterConfig = ReadonlyDeep<{
  spotlightImage: string;
  links: {
    homeLink: string;
  };
  footerColumns?: {
    column1?: FooterColItem[];
    column2?: FooterColItem[];
    column3?: FooterColItem[];
    bottom?: FooterColItem[][];
    joinUsLink?: string;
    footerImageData?: FooterImageData;
  };
  organization: {
    phoneNumber: (isOnPreConPage?: boolean) => string | null;
    email: (isOnPreConPage?: boolean) => string | null;
    address: (siteLocation?: CountryCodeList) => string | null;
    twitterUrl: string;
    facebookUrl: string;
    instagramUrl: string;
    linkedinUrl: string;
    youtubeUrl?: string;
    joinSectionUrl?: string;
  };
  showAccessibilityLink?: boolean;
}>

export type AgentSearchConfig = ReadonlyDeep<{
  headerImage: {
      mobileSrc: string;
      src: string;
      alt: string;
  };
  agentTypeName: string;
  featuredAgentDescriptor: string,
  pageTitle: string;
}>

const MY_LINK_MY_LEAD_FEATURE_NAME = 'MyLinkMyLead' as const;
const AGENT_SEARCH_FEATURE_NAME = 'AgentSearch' as const;

export type Features = {
  [MY_LINK_MY_LEAD_FEATURE_NAME]?: boolean;
  [AGENT_SEARCH_FEATURE_NAME]?: boolean;
}

export type QuoteConfig = ReadonlyDeep<{
  authorImage: string;
  authorImageAlt: string;
  authorTitle: string;
  authorName: string;
  quoteContent: string;
  quoteClassnames?: HtmlTransClassNames;
  ctaText?: string;
  theme?: {
    color: string;
  };
}>

export type ExpCommercialConfig = ReadonlyDeep<{
  namespace: string;
  leoQuoteConfig: QuoteConfig;
}>

// Define the config type
export type JoinExpConfig = ReadonlyDeep<{
  namespace: string;
  joinFormLink: string;
  learnMoreClickEvent: string;
  builtForThisJoinUsClickEvent: string;
  signMeUpClickEvent: string;
  getNowClickEvent: string;
  glennQuoteConfig: QuoteConfig;
  leoQuoteConfig: QuoteConfig;
  videoCardData: Array<{
    videoId: string;
    bottomText: string;
  }>;
  cardStackData: Array<{
    title: string;
    description: string;
    imageSrc: string;
    backgroundColor: string;
    textColor: string;
    imageAlt: string;
  }>;
  programsData: Array<{
    image: string;
    imageAlt: string;
    title: string;
    description: string;
    buttonText: string;
    buttonLink: string;
    buttonClickEvent?: string;
  }>;
  faq: {
    title: string;
    content:Array<{
      title: string;
      children: string;
      styleOverrides?: HtmlTransStyleOVerrides;
      values?: HtmlTransValues;
    }>
  };
}>

export type CommercialBenefitsConfig = ReadonlyDeep<{
  cards: Array<{
    title: string;
    description: string;
    type: 'card';
  } | {
    type: 'video';
    url: string;
  }>;
}>

export type SEOConfig = {
  noFollowRoutes?: Route[]; // Routes to apply nofollow metadata to
};

export type FiltersConfig = {
  hideExpiredListings: boolean; // Determines whether the filter bar will allow users to filter for expired listings
};

export type Tenant<T> = ReadonlyDeep<{
  id: string;
  hosts: string[];
  theme: ThemeConfig; // This is only styles and colors and we need to migrate to MUI theming
  features: Features;
  localization: {
    languages: (string | SiteLanguage)[]; // This is the languages that are supported by the tenant
    countries: (string | CountryCode)[]; // This is the country that the tenant can be rendered in
    namespaces: (string | SiteNameSpace)[]; // This is the i18n namespaces required for the tenant. Files are located in public/locales
    defaultLanguage: SiteLanguage;
  };
  config: {
    home?: HomeConfig;
    header?: HeaderConfig;
    footer?: FooterConfig;
    agentSearch?: AgentSearchConfig;
    joinExp?: JoinExpConfig;
    commercialBenefits?: CommercialBenefitsConfig;
    expCommercial?: ExpCommercialConfig;
    seo?: SEOConfig; // Add SEO configuration
    filters?: FiltersConfig;
  };
  debug:{
    forceTenantHeaderValue?: string;
  }
}>


export function hostToTenant (host: string): TenantName | null {
  // Zoocasa
  if (host.endsWith('zoocasa.com')) 
    return ZOOCASA_TENANT_NAME;
  
  // EXP Realty CA
  if (
    host.endsWith('exprealty.ca') || 
    host.endsWith('zooexptest.ca') || 
    host.endsWith('zooexpprod.ca') ||
    host.endsWith('join-expca.zooexptest.com')
  ) 
    return EXP_REALTY_CA_TENANT_NAME;

  // EXP In Service
  if (
    host.endsWith('inservice.zooexptest.com') || 
    host.endsWith('inservice.exprealty.com')
  )
    return EXP_IN_SERVICE_TENANT_NAME;
  
  // EXP Realty US
  if (
    host.endsWith('exprealty.com') || 
    host.endsWith('join-expus.zooexptest.com')
  )
    return EXP_REALTY_US_TENANT_NAME;
  
  // EXP Commercial
  if (
    host.endsWith('expcommercial.com') || 
    host.endsWith('commercial.zooexptest.com') ||
    host.endsWith('zooexptest.com') || 
    host.endsWith('zooexpprod.com')
  )
    return EXP_COMMERCIAL_TENANT_NAME;
  
  return null;
}
